import CloseIcon from '@mui/icons-material/Close'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'

import { useNavigate } from '@/routing'

import { useStorageAlert } from './hooks/useStorageAlert'
import { useTexts } from './useTexts'

export function StorageIndicator() {
  const { storagePercentage, shouldShow, dismissAlert } = useStorageAlert()
  const navigate = useNavigate<uui.routing.Setup>(true)
  const texts = useTexts()

  const getAlertConfig = () => {
    if (storagePercentage >= 100) {
      return {
        severity: 'error' as const,
        title: texts.fullErrorTitle,
        description: texts.fullErrorDescription,
      }
    } else if (storagePercentage >= 95) {
      return {
        severity: 'error' as const,
        title: texts.errorTitle,
        description: texts.errorDescription,
      }
    } else if (storagePercentage >= 80) {
      return {
        severity: 'warning' as const,
        title: texts.warningTitle,
        description: texts.warningDescription,
      }
    }
    return null
  }

  const alertConfig = getAlertConfig()

  if (!alertConfig || !shouldShow) {
    return null
  }

  const handleClose = () => {
    if (alertConfig?.severity === 'warning') {
      dismissAlert()
    }
  }

  const goToTerritory = () => {
    navigate('territory')
  }

  return (
    <Alert
      severity={alertConfig.severity}
      sx={{
        width: '100%',
        '& .MuiAlert-message': {
          flex: 1,
        },
      }}
      action={
        alertConfig.severity === 'warning' ? (
          <Tooltip title={texts.close}>
            <IconButton color="inherit" size="small" onClick={handleClose}>
              <CloseIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        ) : undefined
      }
    >
      <AlertTitle>{alertConfig.title}</AlertTitle>
      <Stack spacing={1}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
          <LinearProgress
            variant="determinate"
            value={storagePercentage}
            color={alertConfig.severity}
            sx={{
              height: 8,
              flex: 1,
            }}
          />
          <Typography variant="subtitle2" color={alertConfig.severity}>
            {`${storagePercentage}%`}
          </Typography>
        </Stack>

        <Typography variant="body2">{alertConfig.description}</Typography>

        <Box sx={{ paddingLeft: 1 }}>
          <Button
            variant="text"
            sx={{
              padding: 0,
            }}
            onClick={goToTerritory}
          >
            {texts.linkText}
          </Button>
        </Box>
      </Stack>
    </Alert>
  )
}
